import {ProductType} from '@wix/wixstores-client-core';
import type {ICurrentCartService, SiteStore} from '@wix/wixstores-client-storefront-sdk';
import {CreateCheckoutExceptions, FedopsInteractions} from '../../common/constants';
import {
  ApplicationError,
  CartLayout,
  ICartControllerApi,
  IOptionsSelectionsValue,
  IRenderingConfig,
  ModelContext,
} from '../../types/app.types';
import {BIService} from './BIService';
import {StyleSettingsService} from './StyleSettingsService';
import {SPECS} from '../specs';
import {CartModel} from '../models/Cart/Cart.model';
import {LineItemModel} from '../models/LineItems/LineItem.model';
import {CartSummaryModel} from '../models/CartSummary/CartSummary.model';
import {ViolationSeverity} from '../models/CartSummary/Violation.model';
import {
  Cart,
  ChannelType,
  DeliveryLogistics,
  EstimateTotalsResponse,
  MultiCurrencyPrice,
} from '@wix/auto_sdk_ecom_current-cart';
import {EcommerceSettingsService} from './EcommerceSettingsService';
import type {DeliveryOptionsService} from '@wix/delivery-section-cart/services';
import {DeliverySectionModel} from '../models/DeliverySection.model';
import {ControllerParams} from '@wix/yoshi-flow-editor';
import {getFormattingFunction} from '../utils/getFormattingFunction';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';

type CartWithPrivateFields = Cart & {
  subtotalAfterDiscounts: MultiCurrencyPrice;
  subtotal: MultiCurrencyPrice;
  discount: MultiCurrencyPrice;
  paymentCurrency: string;
};
type DeliveryLogisticsWithPrivateFields = DeliveryLogistics & {deliveryTimeSlot: string};

const bookingsAppId = '13d21c63-b5ec-5912-8397-c3a5ddb27a97';

export type CouponError = {
  code: string;
  message: string;
};

type IEnrichedData = {
  renderingConfigMap: Record<string, IRenderingConfig>;
  bookingsOptionsMap: Record<string, IOptionsSelectionsValue[]>;
};

export class CartService {
  private readonly siteStore: SiteStore;
  private readonly viewerContext: ViewerContext;
  private readonly biService: BIService;
  private readonly styleSettingsService: StyleSettingsService;
  private readonly currentCartService: ICurrentCartService;
  private readonly ecommerceSettingsService: EcommerceSettingsService;
  private readonly deliveryOptionsServicePromise: Promise<DeliveryOptionsService>;
  public readonly origin: string;
  public readonly cartLayout: CartLayout;
  public couponError: CouponError = null;
  public cartModel: CartModel;
  public cartSummary: CartSummaryModel;
  public checkoutId: string;
  public hasError: boolean;
  public isSummaryUpdating: boolean = false;
  public isCouponUpdateInProgress: boolean = false;
  public isSummaryLoading: boolean = false;
  public controllerApi: ICartControllerApi;
  public loadingItems: string[] = [];
  public errorOnEstimateTotals: boolean = false;
  public deliverySectionModel: DeliverySectionModel;
  public flowAPI: ControllerParams['flowAPI'];

  constructor({
    siteStore,
    biService,
    styleSettingsService,
    currentCartService,
    origin,
    controllerApi,
    ecommerceSettingsService,
    cartLayout,
    deliveryOptionsServicePromise,
    flowAPI,
    viewerContext,
  }: {
    controllerApi: ICartControllerApi;
    siteStore: SiteStore;
    biService: BIService;
    styleSettingsService: StyleSettingsService;
    currentCartService: ICurrentCartService;
    origin: string;
    ecommerceSettingsService: EcommerceSettingsService;
    cartLayout: CartLayout;
    deliveryOptionsServicePromise: Promise<DeliveryOptionsService>;
    flowAPI: ControllerParams['flowAPI'];
    viewerContext: ViewerContext;
  }) {
    this.siteStore = siteStore;
    this.biService = biService;
    this.styleSettingsService = styleSettingsService;
    this.currentCartService = currentCartService;
    this.origin = origin;
    this.controllerApi = controllerApi;
    this.ecommerceSettingsService = ecommerceSettingsService;
    this.cartLayout = cartLayout;
    this.deliveryOptionsServicePromise = deliveryOptionsServicePromise;
    this.flowAPI = flowAPI;
    this.viewerContext = viewerContext;
  }

  public clearLoadingItems = () => {
    this.loadingItems = [];
  };

  public showLoaderOnItem = (cartItemId: string) => {
    this.loadingItems.push(cartItemId);
    this.isSummaryUpdating = true;
  };

  private hasBookingsItem(cart: Cart) {
    return cart?.lineItems.some((lineItem) => lineItem?.catalogReference?.appId === bookingsAppId);
  }

  private async getEnrichedData(): Promise<IEnrichedData> {
    const cartGql = await this.getCartGqlForBookings();
    const renderingConfigMap = cartGql.items.reduce((acc, item) => {
      acc[item.cartItemId] = item.renderingConfig;
      return acc;
    }, {});
    const bookingsOptionsMap = cartGql.items.reduce((acc, item) => {
      acc[item.cartItemId] = item.optionsSelectionsValues;
      return acc;
    }, {});
    return {renderingConfigMap, bookingsOptionsMap};
  }

  public async fetchCartV1(): Promise<void> {
    const [cart] = await Promise.all([
      this.currentCartService.getCurrentCartV1(),
      this.ecommerceSettingsService.init(),
    ]);
    if (
      !this.viewerContext.isExperimentEnabled(SPECS.DontUseLineItemEnricherInCartAndCheckout) &&
      this.hasBookingsItem(cart)
    ) {
      const {renderingConfigMap, bookingsOptionsMap} = await this.getEnrichedData();
      this.setCartModel(cart, renderingConfigMap, bookingsOptionsMap);
    } else {
      this.setCartModel(cart);
    }
  }

  public async isPreselectedNonShippingFlow(): Promise<boolean> {
    try {
      const estimatedTotals = await this.currentCartService.estimateCurrentCartTotals({
        calculateShipping: true,
        calculateTax: this.shouldCalculateTax(),
      });
      const isPickup =
        !!estimatedTotals.shippingInfo?.selectedCarrierServiceOption?.logistics?.pickupDetails?.address?.addressLine1;
      const hasTimeSlots = Boolean(
        (estimatedTotals.shippingInfo?.selectedCarrierServiceOption?.logistics as DeliveryLogisticsWithPrivateFields)
          ?.deliveryTimeSlot
      );
      return isPickup || hasTimeSlots;
    } catch (e) {
      await this.controllerApi.reportFedops(FedopsInteractions.errorInEstimateTotalsWithShipping);
      return false;
    }
  }

  public async fetchEstimateTotals(): Promise<void> {
    const onlyCallDeliverySolutionsWhenNeededOnCart = this.viewerContext.isExperimentEnabled(
      SPECS.OnlyCallDeliverySolutionsWhenNeededOnCart
    );
    try {
      const estimatedTotals = await this.currentCartService.estimateCurrentCartTotals({
        calculateShipping: this.shouldCalculateShipping(),
        calculateTax: this.shouldCalculateTax(),
      });
      this.setEstimatedTotalModel(estimatedTotals);
      if (!onlyCallDeliverySolutionsWhenNeededOnCart || this.shouldCalculateShipping()) {
        const deliveryOptionsService = await this.deliveryOptionsServicePromise;
        const options = await deliveryOptionsService.getDeliveryOptions(
          {estimateTotals: estimatedTotals},
          estimatedTotals.shippingInfo?.selectedCarrierServiceOption?.code
        );
        this.deliverySectionModel = new DeliverySectionModel(
          options,
          estimatedTotals.shippingInfo?.selectedCarrierServiceOption?.code
        );
      }
      this.errorOnEstimateTotals = false;
      await this.controllerApi.updateComponent();
    } catch {
      this.biService.cartEstimateTotalsErrorShown(this.cartModel, this.cartLayout);
      this.errorOnEstimateTotals = true;
      await this.controllerApi.updateComponent();
    }
  }

  public get hasPickupOption(): boolean {
    return this.deliverySectionModel?.options.some((option) => option.isPickup);
  }

  private sendCartLoadedBi() {
    return this.biService.viewCartPageSf({
      cartLayout: this.cartLayout,
      cartSummary: this.cartSummary,
      cartModel: this.cartModel,
      shouldShowCoupon: this.styleSettingsService.shouldShowCoupon,
      shouldShowBuyerNote: this.styleSettingsService.shouldShowBuyerNote,
      shouldShowContinueShopping: this.styleSettingsService.shouldRenderContinueShopping,
      shouldShowShipping: this.styleSettingsService.shouldShowShipping,
      shouldShowTax: this.styleSettingsService.shouldShowTax,
      hasPickupOption: this.hasPickupOption,
      isCheckoutButtonPresented: this.styleSettingsService.isCheckoutButtonShowInSomeBreakpoint,
      isViewCartButtonPresented: this.styleSettingsService.isViewCartButtonShownInSomeBreakpoint,
      hasGiftCardLineItem: this.cartModel.lineItemsTypes.includes(ProductType.GIFT_CARD),
    });
  }

  public async fetchCartAndEstimateTotals(isInitialLoad = false): Promise<void> {
    await this.fetchCartV1();
    const shouldEstimateTotals = this.shouldEstimateTotals();
    this.isSummaryLoading = shouldEstimateTotals && isInitialLoad;
    this.isSummaryUpdating = shouldEstimateTotals;

    if (!this.viewerContext.isSSR() && shouldEstimateTotals) {
      this.flowAPI.panoramaClient?.transaction(FedopsInteractions.estimateTotals).start();
      void this.fetchEstimateTotals().then(() => {
        this.flowAPI.panoramaClient?.transaction(FedopsInteractions.estimateTotals).finish();
        this.isSummaryUpdating = false;
        this.isSummaryLoading = false;
        void this.controllerApi.updateComponent();
        void this.sendCartLoadedBi();
      });
    } else {
      this.removeEstimatedTotals();
      void this.sendCartLoadedBi();
    }
  }

  private shouldCalculateTax(): boolean {
    const {shouldShowTax} = this.styleSettingsService;
    const moveCartToViewerContext = this.viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext);
    const taxOnProduct = moveCartToViewerContext
      ? this.ecommerceSettingsService.getEcommerceSettings().taxIncludedInItemPrices
      : this.siteStore.priceSettings?.taxOnProduct;
    return shouldShowTax && !taxOnProduct;
  }

  public shouldCalculateShipping(): boolean {
    const {shouldShowShipping} = this.styleSettingsService;
    return shouldShowShipping && !this.isNonShippableCart;
  }

  private isCartWithDepositItem(): boolean {
    return this.cartModel.lineItems.some((lineItem) => lineItem.paymentConfig.depositAmount);
  }

  private hasAnActiveSPI() {
    const {hasValidationsInCart, hasAdditionalFees} = this.ecommerceSettingsService.getEcommerceSettings();
    return hasValidationsInCart || (hasAdditionalFees && this.cartLayout === CartLayout.CART);
  }

  public readonly clearError = async () => {
    this.setHasErrorState(false);
    await this.controllerApi.updateComponent();
  };

  public readonly handleGeneralError = async (error: Error) => {
    this.clearLoadingItems();
    this.setHasErrorState(true);
    this.isSummaryUpdating = false;
    await this.controllerApi.updateComponent();
    this.biService.errorPresentedInCartSideCart(error.message, this.cartModel);
    setTimeout(() => {
      void (async () => {
        await this.clearError();
      })();
    }, 5000);
  };

  public shouldEstimateTotals(): boolean {
    const {isExpressCheckoutButtonShownInSomeBreakpointOrIsEditorMode} = this.styleSettingsService;
    const moveCartToViewerContext = this.viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext);
    const editorMode = moveCartToViewerContext
      ? this.viewerContext.getViewMode() === 'Editor'
      : this.siteStore.isEditorMode();

    return (
      this.cartModel.lineItems.length > 0 &&
      (this.shouldCalculateShipping() ||
        this.shouldCalculateTax() ||
        isExpressCheckoutButtonShownInSomeBreakpointOrIsEditorMode ||
        this.isCartWithDepositItem() ||
        editorMode ||
        this.hasAnActiveSPI() ||
        this.cartModel.hasItemWithFreeTrial ||
        (this.viewerContext.isExperimentEnabled(SPECS.RefactorCouponOnCart) && !!this.cartModel.coupon))
    );
  }

  private getCartGqlForBookings() {
    return this.currentCartService.getCurrentCartGQL({
      withShipping: false,
      withTax: false,
    });
  }

  public get isNonShippableCart(): boolean {
    return !this.hasShippableItems;
  }

  public get hasShippableItems(): boolean {
    if (!this.viewerContext.isExperimentEnabled(SPECS.UseIsShippableInCartPage)) {
      return this.cartModel?.lineItems.some(
        (lineItem) => !lineItem.attributes.itemType || lineItem.attributes.itemType === ProductType.PHYSICAL
      );
    }

    return this.cartModel?.lineItems.some((lineItem) => lineItem.attributes.physicalProperties.shippable);
  }

  public get isZeroCart(): boolean {
    if (this.cartSummary) {
      return this.cartSummary.priceSummary.total.convertedAmount === 0;
    } else {
      return this.viewerContext.isExperimentEnabled(SPECS.RefactorCouponOnCart)
        ? this.cartModel.subtotal.convertedAmount === 0
        : this.cartModel.subtotalAfterDiscounts.convertedAmount === 0;
    }
  }

  public get isEmpty(): boolean {
    return !this.cartModel?.lineItems.length;
  }

  public get areAllItemsInStock(): boolean {
    return (
      this.cartModel?.lineItems &&
      this.cartModel.lineItems.every(
        (lineItem) =>
          lineItem.quantityInfo.availableQuantity === undefined || lineItem.quantityInfo.availableQuantity > 0
      )
    );
  }

  public get isFullAddressRequired() {
    return (
      this.deliverySectionModel?.errors?.hasShippingDestinationError ||
      this.deliverySectionModel?.errors?.hasShippingZipCodeError
    );
  }

  public get itemsCount(): number {
    return this.cartModel.lineItems.reduce((count, lineItem) => count + lineItem.quantityInfo.quantity, 0);
  }

  public get hasErrorViolations(): boolean {
    return this.cartSummary?.violations?.some((violation) => violation.severity === ViolationSeverity.error);
  }

  public async createCheckout(): Promise<string | {error: string} | undefined> {
    try {
      const {checkoutId} = await this.currentCartService.createCheckout({channelType: ChannelType.WEB});
      this.checkoutId = checkoutId;
      return checkoutId;
    } catch (error) {
      if (
        (error as ApplicationError)?.details?.applicationError?.code === CreateCheckoutExceptions.siteMustAcceptPayments
      ) {
        return {error: CreateCheckoutExceptions.siteMustAcceptPayments};
      }
      return this.viewerContext.isExperimentEnabled(SPECS.FixHandleCreateCheckoutError)
        ? undefined
        : {error: CreateCheckoutExceptions.unrecognizedError};
    }
  }

  public readonly updateItemQuantity = async (cartItemId: string, quantity: number): Promise<void> => {
    try {
      return await this.currentCartService.updateLineItemQuantity(
        {lineItemId: cartItemId, quantity},
        {origin: this.origin}
      );
    } catch (e) {
      await this.handleGeneralError(e as Error);
    }
  };

  public readonly updateBuyerNote = async (content: string) => {
    try {
      await this.currentCartService.updateBuyerNote({buyerNote: content});
      this.biService.updateBuyerNote(this.cartModel, !!content);
    } catch (e) {
      await this.handleGeneralError(e as Error);
    }
  };

  public readonly removeItemFromCart = async (lineItem: LineItemModel): Promise<void> => {
    try {
      // eslint-disable-next-line import/no-deprecated
      return await this.currentCartService.removeLineItem({lineItemId: lineItem.id}, {origin: this.origin});
    } catch (e) {
      await this.handleGeneralError(e as Error);
    }
  };

  public readonly setDestinationForEstimation = async ({
    country,
    subdivision,
    zipCode,
  }: {
    country: string;
    subdivision?: string;
    zipCode?: string;
  }): Promise<void> => {
    try {
      return await this.currentCartService.setAddress({address: {country, subdivision, zipCode}});
    } catch (e) {
      await this.handleGeneralError(e as Error);
    }
  };

  public readonly setShippingOption = async (options: {option: {code: string; carrierId: string}}): Promise<void> => {
    try {
      return await this.currentCartService.selectShippingOption(options);
    } catch (e) {
      await this.handleGeneralError(e as Error);
    }
  };

  public readonly clearCouponError = (): void => {
    this.couponError = null;
  };

  public readonly applyCoupon = async (couponCode: string): Promise<void> => {
    try {
      this.clearCouponError();
      this.isSummaryUpdating = true;
      this.isCouponUpdateInProgress = true;
      await this.controllerApi.updateComponent();

      const applyCouponPromise = this.currentCartService.applyCoupon({code: couponCode}, {origin: this.origin});

      await applyCouponPromise.catch((e) => {
        const errorCode =
          e?.details?.applicationError?.code ??
          e?.validationError?.fieldViolations?.[0]?.ruleName ??
          'ERROR_COUPON_GENERAL_ERROR';
        this.couponError = {
          code: errorCode,
          message: e?.details?.applicationError?.description,
        };
        this.biService.errorWhenApplyingACouponSf(this.cartModel, couponCode, errorCode);
        throw e;
      });

      this.isCouponUpdateInProgress = false;
      await this.controllerApi.updateComponent();
    } catch {
      this.isSummaryUpdating = false;
      this.isCouponUpdateInProgress = false;
      return this.controllerApi.updateComponent();
    }
  };

  public readonly removeCoupon = async (): Promise<void> => {
    try {
      this.isSummaryUpdating = true;
      this.isCouponUpdateInProgress = true;
      await this.controllerApi.updateComponent();

      await this.currentCartService.removeCoupon({origin: this.origin});
      this.isCouponUpdateInProgress = false;
      await this.controllerApi.updateComponent();
    } catch (e) {
      this.isCouponUpdateInProgress = false;
      await this.controllerApi.updateComponent();
      await this.handleGeneralError(e as Error);
    }
  };

  public readonly setHasErrorState = (value: boolean) => (this.hasError = value);

  private readonly setCartModel = (
    cart: Cart,
    renderingConfigMap?: Record<string, IRenderingConfig>,
    bookingsOptionsMap?: Record<string, IOptionsSelectionsValue[]>
  ) =>
    (this.cartModel = CartModel.fromCartV1(
      cart as CartWithPrivateFields,
      renderingConfigMap,
      bookingsOptionsMap,
      this.getModelContext(cart.conversionCurrency, cart.paymentCurrency)
    ));

  private readonly setEstimatedTotalModel = (estimatedTotals: EstimateTotalsResponse) =>
    (this.cartSummary = CartSummaryModel.fromCartV1(
      estimatedTotals,
      this.getModelContext(this.cartModel.customerInfo.currencyCode, this.cartModel.paymentInfo.currencyCode)
    ));
  private readonly removeEstimatedTotals = () => (this.cartSummary = undefined);

  private readonly getModelContext = (formatingCurrency: string, paymentCurrency: string): ModelContext => {
    return {
      viewerContext: this.viewerContext,
      formatAmount: getFormattingFunction({flowAPI: this.flowAPI, formatingCurrency}),
      shouldPayWithConvertedCurrency: paymentCurrency === formatingCurrency,
    };
  };

  public reportCheckoutButtonPresented(isButtonShown: boolean): void {
    this.biService.checkoutButtonPresentedToUoU(this.cartModel, isButtonShown);
  }

  public reportCartButtonPresented(isButtonShown: boolean): void {
    this.biService.cartButtonPresentedToUoU(this.cartModel, isButtonShown);
  }

  public reportPromoCodePresented(isPromoCodeShown: boolean): void {
    this.biService.promoCodePresentedToUoU(this.cartModel, isPromoCodeShown);
  }

  public reportBuyerNotePresented(isBuyerNoteShown: boolean): void {
    this.biService.buyerNotePresentedToUoU(this.cartModel, isBuyerNoteShown);
  }
}
